/**
 * Custom hook untuk mengambil data bank
 * 
 * API Endpoint: GET `/api/property-bank`
 * API Version: 3.0
 * 
 * @param {boolean} filters.all - Jika true, maka akan mengambil semua data bank
 * @param {string} property_id - ID property yang ingin diambil
 * @returns {object} - Semua data bank yang telah diambil beserta metadata-nya
 * @example
 * const { data, isLoading, error } = useIndexBank({ filters: { all: 1 } });
 */

import { useQuery } from "@tanstack/react-query";
import { privateApi } from "../../../api/api";
import { IndexBankResponseSchema } from "../response/IndexBankResponse";

const API_VERSION = "v3";

interface FilterProps {
    all?: number;
    property_id?: string;
}

interface GetAllBookingProps {
    filters?: FilterProps,
    search?: string,
}

const useIndexBank = (query: GetAllBookingProps) => {
    return useQuery({
        queryKey: [
            'banks',
            query.filters?.all ? 'all' : '',
            ...Object.entries(query.filters ?? {})
                .filter(([key, value]) => key !== 'all' && value !== undefined && value !== null && value !== "")
                .map(([key, value]) => `${key}:${value}`),
            query.search
        ],
        queryFn: async () => {
            try {
                const params = query.filters?.all
                    ? { all: 1 }
                    : {
                        filter: Object.fromEntries(
                            Object.entries(query.filters ?? {}).filter(([key, value]) => key !== "all" && value !== undefined && value !== null && value !== "")
                        ),
                        search: query.search || ""
                    };

                const response = await privateApi.get(`/${API_VERSION}/property-bank`, { params });

                // Validasi response
                const validationResult = IndexBankResponseSchema.safeParse(response.data);
                if (!validationResult.success) {
                    console.error("Validation failed:", validationResult.error.errors);
                    throw new Error("Invalid banks data format");
                }
                return validationResult.data;
            } catch (error) {
                console.error(`Failed to fetch banks`, error);
                throw error;
            }
        },
        retry: import.meta.env.VITE_MAX_RETRY,
        refetchOnWindowFocus: false,
    });
};

export default useIndexBank;